import { useLingui } from "@lingui/react/macro";
import type { OrchestrationTurnInspection, TurnId } from "@t3tools/contracts";
import {
  getModelSelectionBooleanOptionValue,
  getModelSelectionStringOptionValue,
} from "@t3tools/shared/model";
import { InfoIcon } from "lucide-react";
import { useState } from "react";

import { formatContextWindowTokens } from "~/lib/contextWindow";
import { formatElapsed } from "~/session-logic";
import { Button } from "../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Spinner } from "../ui/spinner";

type InspectionState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "loaded"; readonly inspection: OrchestrationTurnInspection | undefined }
  | { readonly status: "error"; readonly message: string };

export function TurnInspectionButton({
  turnId,
  onLoad,
}: {
  turnId: TurnId;
  onLoad: (turnId: TurnId) => Promise<OrchestrationTurnInspection | undefined>;
}) {
  const { t } = useLingui();
  const [state, setState] = useState<InspectionState>({ status: "idle" });

  const load = () => {
    if (
      state.status === "loading" ||
      (state.status === "loaded" && state.inspection?.completedAt != null)
    ) {
      return;
    }
    setState({ status: "loading" });
    onLoad(turnId).then(
      (inspection) => setState({ status: "loaded", inspection }),
      (error: unknown) =>
        setState({
          status: "error",
          message: error instanceof Error ? error.message : String(error),
        }),
    );
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) {
          load();
        }
      }}
    >
      <PopoverTrigger
        openOnHover
        delay={150}
        closeDelay={0}
        render={
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            aria-label={t`Turn details`}
            className="text-muted-foreground/45 hover:text-muted-foreground/70"
          />
        }
      >
        <InfoIcon className="size-3" />
      </PopoverTrigger>
      <PopoverPopup tooltipStyle side="top" align="start" className="max-w-none p-0">
        <div className="p-3 text-[11px] leading-4" data-turn-inspection="true">
          {state.status === "loaded" && state.inspection ? (
            <TurnInspectionRows inspection={state.inspection} />
          ) : state.status === "error" ? (
            <span className="text-destructive">{state.message}</span>
          ) : (
            <Spinner className="size-3 text-muted-foreground" />
          )}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

function TurnInspectionRows({ inspection }: { inspection: OrchestrationTurnInspection }) {
  const { t } = useLingui();
  const { modelSelection, tokenUsage, startedAt, completedAt } = inspection;
  const effort =
    getModelSelectionStringOptionValue(modelSelection, "reasoningEffort") ??
    getModelSelectionStringOptionValue(modelSelection, "effort");
  const fastMode = getModelSelectionBooleanOptionValue(modelSelection, "fastMode");
  const duration =
    startedAt !== null && completedAt !== null ? formatElapsed(startedAt, completedAt) : null;
  const rows: Array<readonly [label: string, value: string]> = [];
  if (modelSelection) {
    rows.push([t`Model`, modelSelection.model]);
  }
  if (effort !== undefined) {
    rows.push([t`Effort`, effort]);
  }
  if (fastMode !== undefined) {
    rows.push([t`Fast`, fastMode ? "on" : "off"]);
  }
  if (tokenUsage) {
    rows.push(
      [t`Input`, formatContextWindowTokens(tokenUsage.inputTokens)],
      [t`Input (cached)`, formatContextWindowTokens(tokenUsage.cachedInputTokens)],
      [
        t({ message: "Output", context: "token usage" }),
        formatContextWindowTokens(tokenUsage.outputTokens),
      ],
    );
  }
  if (duration !== null) {
    rows.push([t`Duration`, duration]);
  }

  return (
    <dl className="grid grid-cols-[auto_auto] gap-x-6 gap-y-1">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="whitespace-nowrap text-muted-foreground/60">{label}</dt>
          <dd className="whitespace-nowrap text-right font-medium tabular-nums text-muted-foreground/80">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
